import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import CartDrawer from "./CartDrawer";
import logo from "../../assets/images/logo.png";
import {
  Home, LogOut,
  ShoppingBag,
  Phone,
  ShoppingCart,
  ChevronDown,
  LogIn,
  UserPlus,
  Sparkles,
  Zap,
  Dumbbell,
  Gift,
  Menu,
  X,
  UserRound,
} from "lucide-react";
import { authService } from "../../services/authService";
const SHOP_LINKS = [
  { to: "/category/AllProducts", label: "Tous les produits", icon: ShoppingBag },
  { to: "/category/Beauty", label: "Beauté", icon: Sparkles },
  { to: "/category/Electronique", label: "Électronique", icon: Zap },
  { to: "/category/Sports", label: "Sports", icon: Dumbbell },
  { to: "/category/Coffrets", label: "Coffrets", icon: Gift },
];

export default function Navbar() {
  const { user, profile } = useAuth();
  const { count } = useCart();

  const [dropOpen, setDropOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const dropRef = useRef(null);

  const displayName =
    profile?.lastName || user?.displayName || user?.email || "Mon compte";

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropRef.current && !dropRef.current.contains(event.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const closeMenus = () => {
    setDropOpen(false);
    setMenuOpen(false);
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${isActive
      ? "bg-amber-100 text-amber-800"
      : "text-stone-600 hover:bg-stone-100 hover:text-stone-950"
    }`;

  const iconButtonClass =
    "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 transition-all duration-200 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700";

  return (
    <>
      <nav className="sticky top-0 z-40 border-b border-stone-200/80 bg-white/95 shadow-sm backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          {/* LIGNE PRINCIPALE : logo | menu centre | actions */}
          <div className="grid min-h-[76px] grid-cols-[auto_1fr_auto] items-center gap-3">

            {/* GAUCHE : LOGO */}
            <Link
              to="/"
              onClick={closeMenus}
              aria-label="Splendrofiy - Accueil"
              className="flex shrink-0 items-center gap-2 text-lg font-extrabold tracking-tight text-stone-900 sm:text-xl lg:text-2xl"
            >
              <img
                src={logo}
                alt=""
                className="h-8 w-auto object-contain sm:h-10 lg:h-12"
              />
              <span className="hidden sm:inline">
                Splendrofi<span className="text-amber-500">y</span>
              </span>
            </Link>

            {/* CENTRE : MENU DESKTOP */}
            <div className="hidden items-center justify-center gap-2 lg:flex">
              <NavLink to="/" end className={navLinkClass} onClick={closeMenus}>
                <Home size={17} />
                Accueil
              </NavLink>

              <div className="relative" ref={dropRef}>
                <button
                  type="button"
                  onClick={() => setDropOpen((open) => !open)}
                  className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${dropOpen
                    ? "bg-amber-100 text-amber-800"
                    : "text-stone-600 hover:bg-stone-100 hover:text-stone-950"
                    }`}
                  aria-expanded={dropOpen}
                >
                  <ShoppingBag size={17} />
                  Boutique
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${dropOpen ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {dropOpen && (
                  <ul className="absolute left-1/2 top-full z-50 mt-2 w-64 -translate-x-1/2 rounded-2xl border border-stone-200 bg-white p-2 shadow-xl shadow-stone-900/10">
                    {SHOP_LINKS.map(({ to, label, icon: Icon }, index) => (
                      <React.Fragment key={to}>
                        {index === 1 && <li className="my-2 border-t border-stone-100" />}
                        <li>
                          <Link
                            to={to}
                            onClick={closeMenus}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-stone-600 transition hover:bg-amber-50 hover:text-amber-800"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
                              <Icon size={16} />
                            </span>
                            {label}
                          </Link>
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </div>

              <NavLink to="/contact" className={navLinkClass} onClick={closeMenus}>
                <Phone size={17} />
                Contact
              </NavLink>
            </div>

            {/* DROITE : ACTIONS */}
            <div className="flex shrink-0 items-center justify-end gap-2 lg:col-start-3">

              {user ? (
                <>
                  <span
                    title={user.email || displayName}
                    className="hidden max-w-36 truncate rounded-full bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 md:block"
                  >
                    {displayName}
                  </span>

                  <button
                    type="button"
                    onClick={async () => {
                      closeMenus();
                      await authService.logout();
                    }}
                    title="Se déconnecter"
                    aria-label="Se déconnecter"
                    className={`${iconButtonClass} border-red-200 bg-red-50 text-red-600 hover:border-red-300 hover:bg-red-100 hover:text-red-700`}
                  >
                    <LogOut size={20} strokeWidth={1.8} />
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMenus}
                  aria-label="Se connecter"
                  className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full bg-amber-500 px-3 text-xs font-bold text-stone-950 shadow-sm transition hover:bg-amber-400 sm:gap-2 sm:px-4 sm:text-sm"
                >
                  <LogIn size={16} className="sm:size-[17px]" />
                  <span>Se connecter</span>
                </Link>
              )}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                title="Mon panier"
                aria-label={`Ouvrir le panier${count > 0 ? `, ${count} articles` : ""}`}
                className={iconButtonClass}
              >
                <ShoppingCart size={20} strokeWidth={1.8} />
                {count > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white ring-2 ring-white">
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>

              {/* Bouton menu mobile */}
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 text-stone-700 transition hover:border-amber-300 hover:bg-amber-50 lg:hidden"
                aria-expanded={menuOpen}
                aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {menuOpen ? <X size={21} /> : <Menu size={21} />}
              </button>
            </div>
          </div>

          {/* MENU MOBILE */}
          {menuOpen && (
            <div className="flex flex-col gap-2 border-t border-stone-100 py-4 lg:hidden">
              <NavLink to="/" end className={navLinkClass} onClick={closeMenus}>
                <Home size={18} />
                Accueil
              </NavLink>

              <button
                type="button"
                onClick={() => setDropOpen((open) => !open)}
                className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100"
                aria-expanded={dropOpen}
              >
                <span className="flex items-center gap-2">
                  <ShoppingBag size={18} />
                  Boutique
                </span>
                <ChevronDown
                  size={17}
                  className={`transition-transform ${dropOpen ? "rotate-180" : ""}`}
                />
              </button>

              {dropOpen && (
                <ul className="ml-4 space-y-1 border-l-2 border-amber-200 pl-3">
                  {SHOP_LINKS.map(({ to, label, icon: Icon }) => (
                    <li key={to}>
                      <Link
                        to={to}
                        onClick={closeMenus}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-stone-600 transition hover:bg-amber-50 hover:text-amber-800"
                      >
                        <Icon size={17} />
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <NavLink to="/contact" className={navLinkClass} onClick={closeMenus}>
                <Phone size={18} />
                Contact
              </NavLink>


            </div>
          )}
        </div>
      </nav>

      <CartDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}